import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, Params, Router } from '@angular/router';
import { API_URL } from 'src/app/core/constants/api';
import { environment } from 'src/environments/environment';
import {
  IUserDetails,
  IUserDetailsResponse,
} from '../interfaces/user-details.interface';
import { StorageService } from 'src/app/core/cache/storage.service';
import { Constants } from 'src/app/shared/utilities/constants';
import { Observable, Subject } from 'rxjs';
import { MIME_TYPE } from 'src/app/core/constants/mime-types';
import {
  IApiResponse,
  ICampaignsResponse,
} from '../interfaces/common-api.interface';
import { Message, MessageService } from 'primeng/api';
import * as moment from 'moment';
import { CommonMessages } from 'src/app/core/constants/common';
import { ISchedulerFilteredData } from 'src/app/modules/campaign-scheduler/interfaces/scheduler-listing-response.interface';
import { GridService } from '../components/common-grid/common-grid.service';
const REFRESH_TOKEN_TIME = 10 * 60 * 1000;
@Injectable({
  providedIn: 'root',
})
export class CommonService {
  isTemplateFormValid = false;
  selMediaAttachmentType: string;
  enableDownloadSegment$ = new Subject<boolean>();
  generateReport$ = new Subject();
  history: string[] = [];
  timer: any;
  readonly Format = {
    slashDate: 'DD/MM/YYYY',
    dashDate: 'DD-MM-YYYY',
    slashDateTime: 'DD/MM/YYYY HH:mm',
    dashDateTime: 'DD-MM-YYYY HH:mm',
  };

  constructor(
    private readonly http: HttpClient,
    private readonly storageService: StorageService,
    private readonly messageService: MessageService,
    private readonly router: Router,
  ) {
    this.router.events?.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.history.push(event.urlAfterRedirects);
      }
    });
  }

  get templateFormValidity(): boolean {
    return this.isTemplateFormValid;
  }
  set templateFormValidity(val: boolean) {
    this.isTemplateFormValid = val;
  }

  get mediaAttachment(): string {
    return this.selMediaAttachmentType;
  }
  set mediaAttachment(mediaType: string) {
    this.selMediaAttachmentType = mediaType;
  }

  getFilterList(type: string, payload: Params) {
    return this.http.get<any>(
      `${environment.COMM_BASE_URL}${API_URL.DROPDOWN}/${type}`,
      { params: payload },
    );
  }

  getUserData(): IUserDetails {
    if (this.storageService?.getItem(Constants.STORAGE_KEYS.USER_DATA)) {
      return JSON.parse(
        this.storageService.getItem(Constants.STORAGE_KEYS.USER_DATA) ?? '',
      );
    }
    return {} as IUserDetails;
  }

  getReportStatus(startEndTime: any[], campaignId: string) {
    return this.http.get(
      `${environment.REPORTING_BASE_URL}${API_URL.REPORT}/${
        API_URL.GENERATE
      }/file/${campaignId}?startDate=${moment(startEndTime[0]).format(
        'DD-MM-YYYY',
      )}&endDate=${moment(startEndTime[1]).format('DD-MM-YYYY')}`,
      {
        headers: new HttpHeaders({
          hideLoader: 'true',
        }),
        observe: 'response',
      },
    );
  }

  downloadReport(campaignId: string, fileName: string) {
    return this.http.get(
      `${environment.REPORTING_BASE_URL}${API_URL.REPORT}/${API_URL.DOWNLOAD}/${campaignId}/${fileName}`,
      {
        headers: new HttpHeaders({
          Accept: MIME_TYPE.XLSX,
        }),
        responseType: 'blob',
        observe: 'response',
      },
    );
  }

  logout(): Observable<any> {
    return this.http.post(
      `${environment.COMPOSITE_BASE_URL}${API_URL.LOGOUT}`,
      {
        redirectUrl: this.encodeData(environment.LOGOUT_URL),
      },
    );
  }

  loginAndSaveInfo(payload: any): Observable<IUserDetailsResponse> {
    return this.http.post<IUserDetailsResponse>(
      `${environment.COMPOSITE_BASE_URL}${API_URL.LOGIN_AND_SAVE_INFO}`,
      payload,
    );
  }

  loginWithFederation(redirectUrl: string): Observable<any> {
    return this.http.post<any>(
      `${environment.COMPOSITE_BASE_URL}${API_URL.FEDERATION_LOGIN}`,
      { redirectUrl: this.encodeData(redirectUrl) },
    );
  }

  fetchScreenPermission(): Observable<any> {
    return this.http.get(
      `${environment.COMPOSITE_BASE_URL}${API_URL.SCREEN_PERMISSION}`,
    );
  }

  getCampaigns(): Observable<ICampaignsResponse> {
    return this.http.get<ICampaignsResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.LIST}`,
    );
  }

  showError(errorResponse: HttpErrorResponse): void {
    if (errorResponse?.status === 504) {
      this.showErrorMessage(
        'Currently services are down. Please try again after some time!',
      );
    } else if (errorResponse?.status === 403) {
      this.showErrorMessage(
        errorResponse?.error?.message ||
          'User is not authorized to access this resource',
        {
          sticky: true,
        },
      );
    } else if (errorResponse?.error?.errors?.length) {
      this.showErrorMessage(
        errorResponse?.error?.errors[0].errorMessage || 'Something went wrong!',
      );
    } else {
      this.showErrorMessage(CommonMessages.ERROR.SERVICE_UNAVILABLE_ERROR);
    }
  }

  showToast(message: Message, propertyObj?: object): void {
    if (propertyObj) {
      message = Object.assign(message, propertyObj);
    }
    this.messageService.add(message);
  }

  showSuccessMessage(message: string, propertyObj?: object): void {
    const messageObj = {
      severity: 'success',
      detail: message,
    };
    this.showToast(messageObj, propertyObj);
  }

  showInfoMessage(message: string, propertyObj?: object): void {
    const messageObj = {
      severity: 'info',
      detail: message,
    };
    this.showToast(messageObj, propertyObj);
  }

  showWarningMessage(message: string, propertyObj?: object): void {
    const messageObj = {
      severity: 'warn',
      detail: message,
    };
    this.showToast(messageObj, propertyObj);
  }

  showErrorMessage(message: string, propertyObj?: object): void {
    const messageObj = {
      severity: 'error',
      detail: message,
    };
    this.showToast(messageObj, propertyObj);
  }

  encodeData(data: string) {
    return btoa(data);
  }

  decodeData(encodedData: string) {
    try {
      return atob(encodedData);
    } catch (error) {
      return '';
    }
  }

  getPreviousUrl(): string {
    if (this.history.length) {
      return this.history[this.history.length - 1];
    }
    return '/home';
  }

  clearHistory() {
    this.history = [];
  }

  initializeRefreshTokenActivity() {
    const dToken = this.decodeJwtToken();
    const currentTimeinMillies = new Date().getTime();
    const leftTimeInMillies =
      dToken.exp * 1000 - currentTimeinMillies - REFRESH_TOKEN_TIME;
    if (leftTimeInMillies <= REFRESH_TOKEN_TIME) {
      this.refreshToken();
      return;
    }
    this.setTimerForRefreshToken(leftTimeInMillies);
  }

  setTimerForRefreshToken(time: number) {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      this.refreshToken();
    }, time);
  }

  async refreshToken() {
    let refreshToken = this.storageService.getParsedItem(
      Constants.STORAGE_KEYS.REFRESH_TOKEN,
    );
    if (refreshToken) {
      const body = {
        refreshToken: refreshToken,
        redirectUrl: this.encodeData(environment.REDIRECT_URL),
      };

      this.loginAndSaveInfo(body).subscribe({
        next: (userDetailsResponse: IUserDetailsResponse) => {
          if (!userDetailsResponse.error && userDetailsResponse.data) {
            this.storageService.setItem(
              Constants.STORAGE_KEYS.API_KEY,
              userDetailsResponse.data['apiKey'],
            );
            this.storageService.setItem(
              Constants.STORAGE_KEYS.ID_TOKEN,
              userDetailsResponse.data.idToken,
            );
            this.storageService.setItem(
              Constants.STORAGE_KEYS.VALID_UPTO,
              userDetailsResponse.data.validUpto,
            );
            this.storageService.setItem(Constants.STORAGE_KEYS.USER_DATA, {
              emailId: userDetailsResponse.data.emailId,
              firstName: userDetailsResponse.data.firstName,
              lastName: userDetailsResponse.data.lastName,
            });
            this.initializeRefreshTokenActivity();
          } else {
            this.showErrorMessage(
              userDetailsResponse?.errors?.[0]?.errorMessage ?? 'Error',
            );
            this.logoutAndNavigateToLogin();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.showError(error);
          this.logoutAndNavigateToLogin();
        },
      });
    } else {
      this.logoutAndNavigateToLogin();
    }
  }

  decodeJwtToken() {
    const token = localStorage.getItem(Constants.STORAGE_KEYS.ID_TOKEN);
    if (token) {
      const base64Url = token.split('.')[1];
      const base64 = base64Url?.replace(/-/g, '+')?.replace(/_/g, '/');
      const decodedPayload = window.atob(base64);
      return JSON.parse(decodedPayload);
    }
    return null;
  }

  logoutAndNavigateToLogin() {
    this.logout().subscribe({
      next: (response: IApiResponse) => {
        if (!response.error) {
          this.storageService.loginLogout.next('logout');
          this.storageService.clear();
          this.router.navigate(['login']);
        } else {
          this.showErrorMessage(
            response?.errors?.[0]?.errorMessage ||
              'Logout failed. Please try again.',
          );
        }
      },
      error: (error) => {
        this.showErrorMessage('Network error during logout. Please try again.');
      },
    });
  }

  getEndDateStartTime(startDateTime: string, endDateTime: string): string {
    const startTime = startDateTime?.split(' ')[1];
    const endDate = endDateTime?.split(' ')[0];
    return `${endDate} ${startTime}`;
  }

  capitalizeFirstLetter(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  extractTextFromHTML(htmlText: string): string {
    if (!htmlText) return '';
    const span = document.createElement('span');
    span.innerHTML = htmlText;
    return span.textContent ?? span.innerText;
  }

  toTitleCase(text: string): string {
    return text
      .toLowerCase()
      .split(' ')
      .map((word: string) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  sortingData(data: any, key: string) {
    if (data?.length) {
      return data.sort((a: any, b: any) => {
        return a[key].localeCompare(b[key]);
      });
    }
  }

  resetAndNavigateToLogin() {
    this.storageService.clear();
    this.storageService.loginLogout.next('logout');
    this.router.navigate(['login']);
  }

  isTokenExpired(token: string): boolean {
    if (token === '') return true;
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiry = payload.exp * 1000; // convert to ms
    return Date.now() > expiry;
  }

  async convertFileToBase64File(rawFile: File): Promise<File> {
    const dataUrl = await this.getBase64FromFile(rawFile);
    const base64Payload = dataUrl.includes(',')
      ? dataUrl.split(',')[1]
      : dataUrl;
    const cleaned = base64Payload.replace(/\s+/g, '');

    return new File([cleaned], rawFile?.name, {
      type: 'text/plain',
      lastModified: Date.now(),
    });
  }

  getBase64FromFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (reader.result !== null) {
          resolve(reader.result as string);
        } else {
          reject(new Error('FileReader result is null'));
        }
      };
      reader.onerror = reject;
    });
  }
}
